// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import TextInput from '../Form/TextInput';
import FormDialog from './FormDialog';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  onReject: ((reason: string) => Promise<unknown>) | undefined;
  disableReason?: boolean;
  disableDialog?: boolean;
};

export const RejectDialog = ({
  disableDialog,
  open,
  onClose,
  onReject,
  disableReason,
}: DialogProps) => {
  const [rejectReason, setRejectReason] = useState<string>('');

  const resetState = useCallback(() => {
    setRejectReason('');
  }, [setRejectReason]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(() => {
    if (onReject) {
      return onReject(rejectReason).finally(() => {
        handleClose();
      });
    }
    handleClose();
    return Promise.resolve();
  }, [handleClose, onReject, rejectReason]);

  const disableSubmit = disableReason ? false : !rejectReason;
  return (
    <FormDialog
      open={open}
      disableDialog={disableDialog}
      title="Reject Request"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Reject"
      isSubmitDisabled={disableSubmit}
    >
      {!disableReason && (
        <TextInput label="Reason" value={rejectReason} setValue={setRejectReason} required />
      )}
    </FormDialog>
  );
};
